import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import SpecificationHistory from './SpecificationHistory';
import Pressures from './Pressures';
import ClinicRecordsMisc from './ClinicRecordsMisc';
import { useCallback, useEffect, useRef, useState } from 'react';
import AddNewPrescreening from './AddNewPrescreening';
import { PrescreeningService } from '../../../api/prescreening.service';
import { enqueueSnackbar } from 'notistack';
import PrescreeningImageTile from './PrescreeningImageTile';
import { RHFTextField } from '../../../components/hook-form';
import { useForm } from 'react-hook-form';
import FormProvider from '../../../components/hook-form/FormProvider';
import { useClinicalRecordLock } from '../../../components/clinical-record-lock';
import AutoRefractor from './AutoRefractor';
import { IPatientSymptomsSchema } from '../../../@types/patientSymptoms';
import { PreviousRecords } from './Examination/PreviousRecords';
import { useParams } from 'react-router-dom';
import { DeviceReadingBanner } from '../../../components/hardware/DeviceReadingBanner';
import { DeviceReading } from '../../../hooks/useDeviceEvents';
import { UploadService } from '../../../api/upload.service';
import { getR2File } from '../../../utils/file';

type Props = {
  data: any;
  tab: any;
  clinicalRecordId: string;
  onUpdate: any;
  setNewClinicalId: (newClinicalId: string) => void;
  onUpdatePayload: (payload: any) => void;
  setShowCLForm: (type: boolean) => void;
  setHistory: (history: IPatientSymptomsSchema) => void;
};

/** Sections of the Pre-Screening screen that hold images. */
type PrescreeningImageSection = 'visuals' | 'oct' | 'fundus';

type TileImage = {
  /** What the tile shows: an object URL for a new pick, the R2 URL once saved. */
  preview: string;
  /** Stored URL. Present for a saved image, and after a successful upload. */
  url?: string;
  /** The picked file, still waiting to be uploaded. */
  file?: File;
};

type ImageState = {
  [inputId: string]: TileImage | undefined;
};

/** One image per tile, as the API stores it. */
type PrescreeningImagePayload = {
  section: PrescreeningImageSection;
  slot: number;
  fileUrl: string;
};

type SavedPrescreeningImage = {
  section?: string | null;
  slot?: number | null;
  fileUrl?: string | null;
};

/**
 * The tiles, in the order they are rendered. The tile position inside its
 * section is the `slot` the backend stores, so an image reopens on its own tile.
 */
const IMAGE_SECTIONS: readonly {
  readonly section: PrescreeningImageSection;
  readonly inputIds: readonly string[];
}[] = [
  { section: 'visuals', inputIds: ['input1', 'input2', 'input3', 'input4'] },
  { section: 'oct', inputIds: ['input5', 'input6', 'input7', 'input8'] },
  { section: 'fundus', inputIds: ['input9', 'input10', 'input11', 'input12'] },
];

const TILE_BY_SECTION_SLOT = new Map<string, string>(
  IMAGE_SECTIONS.flatMap(({ section, inputIds }) =>
    inputIds.map(
      (inputId, index) => [`${section}:${index + 1}`, inputId] as const,
    ),
  ),
);

const MAX_IMAGE_MB = 5;
const MAX_IMAGE_BYTES = MAX_IMAGE_MB * 1024 * 1024;

/** Stateless: one instance for the module, not one per render. */
const uploadService = new UploadService();

/** Rebuilds the tiles from the images saved against the pre-screening. */
const tilesFromRecord = (
  images?: readonly SavedPrescreeningImage[] | null,
): ImageState => {
  const tiles: ImageState = {};
  (images ?? []).forEach((image) => {
    if (!image?.fileUrl || !image?.section || !image?.slot) return;
    const inputId = TILE_BY_SECTION_SLOT.get(`${image.section}:${image.slot}`);
    if (!inputId) return;
    tiles[inputId] = {
      preview: getR2File(image.fileUrl) ?? image.fileUrl,
      url: image.fileUrl,
    };
  });
  return tiles;
};

export default function PatientPrescreening({
  tab,
  data,
  clinicalRecordId,
  onUpdate,
  setNewClinicalId,
  onUpdatePayload,
  setShowCLForm,
  setHistory,
}: Props) {
  const lock = useClinicalRecordLock();
  const [showAddPrescreeningModal, setShowAddPrescreeningModal] =
    useState(false);
  const [dataPrescreening, setDataPrescreening] = useState<any>({});
  const [newRecord, setNewRecord] = useState(false);
  // Read inside the `data` effect without re-running it when this toggles.
  const newRecordRef = useRef(newRecord);
  newRecordRef.current = newRecord;
  const [openNewRecord, setOpenNewRecord] = useState(false);
  const [selectedImage, setSelectedImage] = useState<ImageState>({});
  const [averages, setAverages] = useState({
    IOPs: 0,
    CCTs: 0,
  });
  const [selectedChips, setSelectedChips] = useState<{
    reasonForVisit: string[];
    hobbies: string[];
  }>({
    reasonForVisit: [],
    hobbies: [],
  });
  const [openModal, setOpenModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { patientId } = useParams();

  const methods = useForm({
    defaultValues: {
      description: '',
    },
  });
  const { reset: resetVisualsForm, getValues: getVisualsValues } = methods;

  // Object URLs created for previews, revoked when the screen goes away.
  const objectUrlsRef = useRef<string[]>([]);
  useEffect(
    () => () => {
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrlsRef.current = [];
    },
    [],
  );

  const handleImageChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, inputId: string) => {
      const file = e.target.files?.[0];
      // Let the same file be picked again after a rejected or replaced choice.
      e.target.value = '';
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        enqueueSnackbar('Only image files can be added to these tiles.', {
          variant: 'error',
        });
        return;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        enqueueSnackbar(
          `"${file.name}" is larger than ${MAX_IMAGE_MB} MB. Please add a smaller image.`,
          { variant: 'error' },
        );
        return;
      }
      const preview = URL.createObjectURL(file);
      objectUrlsRef.current.push(preview);
      setSelectedImage((prevImages) => ({
        ...prevImages,
        [inputId]: { preview, file },
      }));
    },
    [],
  );

  useEffect(() => {
    const d = data as Record<string, unknown> | undefined | null;
    const isEmptyPlaceholder =
      d &&
      typeof d === 'object' &&
      !Array.isArray(d) &&
      Object.keys(d).length === 0;

    if (isEmptyPlaceholder) {
      // The opened record has no prescreening: clear what the previous record
      // showed, unless the user is drafting a brand-new record here.
      if (!newRecordRef.current) {
        setDataPrescreening({});
        setSelectedChips({ reasonForVisit: [], hobbies: [] });
        setSelectedImage({});
        resetVisualsForm({ description: '' });
      }
      return;
    }

    if (d && typeof d === 'object' && typeof d.id === 'number') {
      setDataPrescreening(data);
      setSelectedChips({
        reasonForVisit: data.reasonForVisit
          ? String(data.reasonForVisit)
              .split(',')
              .map((s: string) => s.trim())
              .filter(Boolean)
          : [],
        hobbies: data.hobbies
          ? String(data.hobbies)
              .split(',')
              .map((s: string) => s.trim())
              .filter(Boolean)
          : [],
      });
      // Saved images go back on the tile they were added to.
      setSelectedImage(tilesFromRecord(data.images));
      resetVisualsForm({ description: data.visualsDescription ?? '' });
      return;
    }

    if (!d) {
      const raw = localStorage.getItem('draftPrescreening');
      if (raw) {
        try {
          const parsed = JSON.parse(raw) as {
            reasonForVisit?: string;
            hobbies?: string;
          };
          setDataPrescreening(parsed);
          setSelectedChips({
            reasonForVisit: parsed.reasonForVisit
              ? String(parsed.reasonForVisit)
                  .split(',')
                  .map((s: string) => s.trim())
                  .filter(Boolean)
              : [],
            hobbies: parsed.hobbies
              ? String(parsed.hobbies)
                  .split(',')
                  .map((s: string) => s.trim())
                  .filter(Boolean)
              : [],
          });
        } catch {
          // ignore invalid draft
        }
      }
    }
  }, [data, resetVisualsForm]);

  const handleOpen = () => {
    setShowAddPrescreeningModal(true);
  };

  const handleClose = () => {
    setShowAddPrescreeningModal(false);
    setOpenNewRecord(false);
  };

  const handleData = (_data: any) => {
    setDataPrescreening(_data);
  };

  /**
   * Uploads every tile whose file hasn't reached R2 yet and returns the images
   * to store. A tile that fails to upload is reported to the user by name; the
   * rest of the pre-screening is still saved.
   */
  const uploadPendingImages = async (): Promise<{
    images: PrescreeningImagePayload[];
    failures: string[];
  }> => {
    const pending = Object.entries(selectedImage).filter(
      ([, tile]) => tile?.file && !tile?.url,
    ) as [string, TileImage][];

    const uploadedUrls: Record<string, string> = {};
    const failures: string[] = [];

    if (pending.length) {
      const results = await Promise.allSettled(
        pending.map(([, tile]) =>
          uploadService.uploadFile(tile.file as File, 'prescreening'),
        ),
      );
      results.forEach((result, index) => {
        const [inputId, tile] = pending[index];
        const url =
          result.status === 'fulfilled' ? result.value?.data?.url : undefined;
        if (url) {
          uploadedUrls[inputId] = url;
          return;
        }
        const message =
          result.status === 'rejected'
            ? (result.reason as { message?: string })?.message
            : 'the server did not return a file URL';
        failures.push(
          `${tile.file?.name ?? inputId}${message ? ` (${message})` : ''}`,
        );
      });

      if (Object.keys(uploadedUrls).length) {
        // Keep the uploaded URLs so a retry doesn't upload the same file twice.
        setSelectedImage((prevImages) => {
          const next = { ...prevImages };
          Object.entries(uploadedUrls).forEach(([inputId, url]) => {
            const tile = next[inputId];
            if (tile) next[inputId] = { ...tile, url, file: undefined };
          });
          return next;
        });
      }
    }

    const images: PrescreeningImagePayload[] = [];
    IMAGE_SECTIONS.forEach(({ section, inputIds }) => {
      inputIds.forEach((inputId, index) => {
        const fileUrl = uploadedUrls[inputId] ?? selectedImage[inputId]?.url;
        if (fileUrl) images.push({ section, slot: index + 1, fileUrl });
      });
    });

    return { images, failures };
  };

  const createClinicalRecord = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const payload: any = { ...dataPrescreening };
      if (
        Array.isArray(selectedChips.hobbies) &&
        selectedChips.hobbies.length > 0
      ) {
        payload.hobbies = selectedChips.hobbies.join(',');
      }

      if (
        Array.isArray(selectedChips.reasonForVisit) &&
        selectedChips.reasonForVisit.length > 0
      ) {
        payload.reasonForVisit = selectedChips.reasonForVisit.join(',');
      }

      const reasonMissing =
        payload.reasonForVisit === undefined ||
        payload.reasonForVisit === null ||
        String(payload.reasonForVisit).trim() === '';
      if (reasonMissing && typeof window !== 'undefined') {
        try {
          const raw = localStorage.getItem('draftPrescreening');
          if (raw) {
            const draft = JSON.parse(raw) as { reasonForVisit?: string };
            const fromDraft = draft?.reasonForVisit?.trim?.();
            if (fromDraft) {
              payload.reasonForVisit = fromDraft;
            }
          }
        } catch {}
      }

      const hobbiesMissing =
        payload.hobbies === undefined ||
        payload.hobbies === null ||
        String(payload.hobbies).trim() === '';
      if (hobbiesMissing && typeof window !== 'undefined') {
        try {
          const raw = localStorage.getItem('draftPrescreening');
          if (raw) {
            const draft = JSON.parse(raw) as { hobbies?: string };
            const fromDraft = draft?.hobbies?.trim?.();
            if (fromDraft) {
              payload.hobbies = fromDraft;
            }
          }
        } catch {
          // ignore invalid draft
        }
      }

      payload.eyePressures?.forEach((pressure: any) => delete pressure.id);
      payload.specificationHistories?.forEach((history: any) => {
        delete history.id;
      });

      delete payload.createdDate;
      delete payload.updatedDate;
      delete payload.id;
      delete payload.createdBy;
      delete payload.updatedBy;

      if (clinicalRecordId && !newRecord) {
        payload.clinicalRecordId = clinicalRecordId;
      }

      // Images: upload what the user picked, then send URLs (never the rows the
      // record was loaded with, which carry ids the API rejects).
      const { images, failures } = await uploadPendingImages();
      payload.images = images;
      if (failures.length) {
        enqueueSnackbar(
          `${failures.length} image${
            failures.length > 1 ? 's' : ''
          } could not be uploaded and ${
            failures.length > 1 ? 'were' : 'was'
          } not saved: ${failures.join('; ')}`,
          { variant: 'warning' },
        );
      }

      // The Description box above the Visuals tiles belongs to the record.
      const visualsDescription = getVisualsValues('description')?.trim() ?? '';
      payload.visualsDescription = visualsDescription || null;

      // Amending a signed record: stage the change instead of saving it.
      if (!newRecord && lock.stageIfAmending('prescreening', payload)) {
        localStorage.removeItem('draftPrescreening');
        tab('history_and_symptoms');
        return;
      }

      const preScreeningService = new PrescreeningService();
      const response: any = await preScreeningService.createNewClinicalRecord(
        patientId,
        payload,
      );
      enqueueSnackbar('Prescreening Created, Please add History And Symptoms', {
        variant: 'success',
      });
      setNewRecord(false);
      // Select the new record first so the (single) list refresh reloads it.
      setNewClinicalId(response?.data?.clinicalRecordId);
      onUpdate();
      tab('history_and_symptoms');
      localStorage.removeItem('draftPrescreening');
      onUpdatePayload(payload?.axis);
      setHistory({} as IPatientSymptomsSchema);
    } catch (error) {
      enqueueSnackbar(
        (error as { message?: string })?.message || 'Something Went Wrong',
        { variant: 'error' },
      );
    } finally {
      setIsSaving(false);
    }
  };

  const setAverageEyePressures = (type: string, value: number) => {
    setAverages((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  const handleDeviceAccepted = (reading: DeviceReading) => {
    if (reading.deviceType === 'TONOREF') {
      const ar = reading.data.autoRef;
      const iop = reading.data.iop;
      setDataPrescreening((prev: any) => ({
        ...prev,
        autoRefractor: {
          ...(prev?.autoRefractor || {}),
          rightSphericalPower: String(ar?.right?.sphere ?? ''),
          rightCylinder: String(ar?.right?.cylinder ?? ''),
          rightAxis: String(ar?.right?.axis ?? ''),
          rightDistancePD: String(ar?.right?.pd ?? ''),
          leftSphericalPower: String(ar?.left?.sphere ?? ''),
          leftCylinder: String(ar?.left?.cylinder ?? ''),
          leftAxis: String(ar?.left?.axis ?? ''),
          leftDistancePD: String(ar?.left?.pd ?? ''),
        },
        eyePressures: [
          ...(prev?.eyePressures || []),
          { IOPs: iop?.right?.mean ?? '', CCT: '', instrument: 'TONOREF II' },
          { IOPs: iop?.left?.mean ?? '', CCT: '', instrument: 'TONOREF II' },
        ],
      }));
    } else if (reading.deviceType === 'LENSMETER') {
      const d = reading.data;
      setDataPrescreening((prev: any) => ({
        ...prev,
        specificationHistories: [
          {
            ...(prev?.specificationHistories?.[0] || {
              condition: 'Average',
              notes: '',
              specifications: '',
            }),
            rightSphericalPower: String(d.right?.sphere ?? ''),
            rightCylinder: String(d.right?.cylinder ?? ''),
            rightAxis: String(d.right?.axis ?? ''),
            rightDistancePD: String(d.right?.pd ?? ''),
            leftSphericalPower: String(d.left?.sphere ?? ''),
            leftCylinder: String(d.left?.cylinder ?? ''),
            leftAxis: String(d.left?.axis ?? ''),
            leftDistancePD: String(d.left?.pd ?? ''),
          },
          ...(prev?.specificationHistories?.slice(1) || []),
        ],
      }));
    }
    setShowAddPrescreeningModal(true);
  };

  return (
    <Grid mt={3} item xs={8} lg={8}>
      <DeviceReadingBanner
        patientId={patientId}
        onAccepted={handleDeviceAccepted}
      />
      <Box></Box>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
        mb={2}
      >
        <Typography sx={{ ml: 1 }} color={'red'}>
          {dataPrescreening
            ? '* Adding a new Pre Screening against a clinical record, please save and continue!'
            : ''}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 2,
          }}
        >
          {!lock.isAmending && (
            <Button
              variant="contained"
              color="primary"
              sx={{ mr: 2 }}
              onClick={() => {
                setOpenNewRecord(true);
                setNewRecord(true);
                setShowCLForm(true);
                handleOpen();
              }}
            >
              Add Clinical Record
            </Button>
          )}
          <Button
            data-lock-hide
            sx={{ mr: 2 }}
            variant="contained"
            color="primary"
            onClick={() => {
              setOpenModal(true);
            }}
          >
            Add From Previous Records
          </Button>
          {dataPrescreening && Object.keys(dataPrescreening).length ? (
            <Button
              data-lock-hide={newRecord ? undefined : true}
              sx={{ mr: 2 }}
              variant="contained"
              color="primary"
              onClick={() => {
                handleOpen();
              }}
            >
              {dataPrescreening ? 'Edit' : 'Add New Clinical Record'}
            </Button>
          ) : (
            <></>
          )}
        </Box>
      </Box>

      <Stack
        spacing={{ xs: 1, sm: 2 }}
        direction="column"
        useFlexGap
        flexWrap="wrap"
      >
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          <SpecificationHistory
            specsData={
              dataPrescreening
                ? [dataPrescreening]
                : data !== undefined
                  ? data
                  : []
            }
          />
          <AutoRefractor data={dataPrescreening?.autoRefractor} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          <Pressures
            eyePressureData={
              dataPrescreening
                ? dataPrescreening.eyePressures
                : data?.eyePressures
            }
            averages={averages}
          />
          <ClinicRecordsMisc
            misc={dataPrescreening ? [dataPrescreening] : data}
          />
        </Box>
      </Stack>
      <Box
        sx={{
          width: '97%',
          margin: '10px',
          padding: '10px',
          borderRadius: '15px',
          boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
        }}
      >
        <Typography variant="h6">Visuals</Typography>
        <FormProvider methods={methods}>
          <RHFTextField
            sx={{
              width: '100%',
              margin: '0 auto',
            }}
            name="description"
            label="Description"
            disabled={lock.isLocked}
          />
        </FormProvider>
        <Stack
          spacing={5}
          alignItems="center"
          justifyContent="center"
          direction={{
            xs: 'column',
            md: 'row',
          }}
          sx={{
            width: '98%',
            textAlign: {
              xs: 'center',
              md: 'left',
            },
          }}
        >
          <PrescreeningImageTile
            inputId="input1"
            src={selectedImage['input1']?.preview}
            disabled={lock.isLocked}
            onChange={handleImageChange}
          />
          <PrescreeningImageTile
            inputId="input2"
            src={selectedImage['input2']?.preview}
            disabled={lock.isLocked}
            onChange={handleImageChange}
          />
          <PrescreeningImageTile
            inputId="input3"
            src={selectedImage['input3']?.preview}
            disabled={lock.isLocked}
            onChange={handleImageChange}
          />
          <PrescreeningImageTile
            inputId="input4"
            src={selectedImage['input4']?.preview}
            disabled={lock.isLocked}
            onChange={handleImageChange}
          />
        </Stack>
      </Box>

      <Box
        sx={{
          width: '97%',
          margin: '10px',
          padding: '10px',
          borderRadius: '15px',
          boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
        }}
      >
        <Typography variant="h6">OCT</Typography>
        <Stack
          spacing={5}
          alignItems="center"
          justifyContent="center"
          direction={{
            xs: 'column',
            md: 'row',
          }}
          sx={{
            width: '98%',
            textAlign: {
              xs: 'center',
              md: 'left',
            },
          }}
        >
          <PrescreeningImageTile
            inputId="input5"
            src={selectedImage['input5']?.preview}
            disabled={lock.isLocked}
            onChange={handleImageChange}
          />
          <PrescreeningImageTile
            inputId="input6"
            src={selectedImage['input6']?.preview}
            disabled={lock.isLocked}
            onChange={handleImageChange}
          />
          <PrescreeningImageTile
            inputId="input7"
            src={selectedImage['input7']?.preview}
            disabled={lock.isLocked}
            onChange={handleImageChange}
          />
          <PrescreeningImageTile
            inputId="input8"
            src={selectedImage['input8']?.preview}
            disabled={lock.isLocked}
            onChange={handleImageChange}
          />
        </Stack>
      </Box>

      <Box
        sx={{
          width: '97%',
          margin: '10px',
          padding: '10px',
          borderRadius: '15px',
          boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
        }}
      >
        <Typography variant="h6">Fundus Photography</Typography>
        <Stack
          spacing={5}
          alignItems="center"
          justifyContent="center"
          direction={{
            xs: 'column',
            md: 'row',
          }}
          sx={{
            width: '98%',
            textAlign: {
              xs: 'center',
              md: 'left',
            },
          }}
        >
          <PrescreeningImageTile
            inputId="input9"
            src={selectedImage['input9']?.preview}
            disabled={lock.isLocked}
            onChange={handleImageChange}
          />
          <PrescreeningImageTile
            inputId="input10"
            src={selectedImage['input10']?.preview}
            disabled={lock.isLocked}
            onChange={handleImageChange}
          />
          <PrescreeningImageTile
            inputId="input11"
            src={selectedImage['input11']?.preview}
            disabled={lock.isLocked}
            onChange={handleImageChange}
          />
          <PrescreeningImageTile
            inputId="input12"
            src={selectedImage['input12']?.preview}
            disabled={lock.isLocked}
            onChange={handleImageChange}
          />
        </Stack>
      </Box>

      {/* <Box
        sx={{
          height: "200px",
          width: "97%",
          margin: "10px",
          borderRadius: "15px",
          boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.16)",
        }}
      >
        <Paper style={{ width: 300, height: 200, padding: 16 }}>
          {selectedImage ? (
            <img
              src={URL.createObjectURL(selectedImage)}
              alt="Uploaded Preview"
              style={{ maxWidth: "100%", maxHeight: "100%" }}
            />
          ) : (
            <Typography variant="body1">Select an image to preview</Typography>
          )}
        </Paper>
        <input type="file" accept="image/*" onChange={handleImageChange} />
      </Box> */}
      {dataPrescreening && (
        <LoadingButton
          data-lock-hide={newRecord ? undefined : true}
          sx={{ mt: 2, mr: 2, float: 'right' }}
          variant="contained"
          color="primary"
          loading={isSaving}
          onClick={createClinicalRecord}
        >
          {lock.isAmending && !newRecord ? 'Add to amendment' : 'Create'}
        </LoadingButton>
      )}
      <Button
        sx={{ mt: 2, mr: 2, float: 'right' }}
        variant="outlined"
        color="primary"
        onClick={() => {
          tab('history_and_symptoms');
        }}
      >
        Next
      </Button>
      <Dialog
        fullWidth
        maxWidth="lg"
        open={showAddPrescreeningModal}
        onClose={handleClose}
      >
        <DialogTitle>Add Pre Screening</DialogTitle>
        <AddNewPrescreening
          onCancel={handleClose}
          onSubmitForm={(data: any) => {
            handleData(data);
          }}
          draftPrescreening={
            openNewRecord ? null : dataPrescreening && dataPrescreening
          }
          setAverages={setAverageEyePressures}
          averages={averages}
          selectedChips={selectedChips}
          setSelectedChips={setSelectedChips}
          setDataPrescreening={(data: any) => setDataPrescreening(data)}
        />
      </Dialog>
      <Dialog
        fullWidth
        maxWidth="md"
        open={openModal}
        onClose={() => setOpenModal(false)}
      >
        <DialogTitle> Previous Records</DialogTitle>
        <PreviousRecords
          selectedRow={clinicalRecordId}
          setPreviousRecordData={setDataPrescreening}
          tab="prescreening"
        />
      </Dialog>
    </Grid>
  );
}
